import AIChatWidget from '@/components/aichat/AiChatWidget';
import ContentFeatureCard from '@/components/landing/content-feature-card';
import InstituteSocialActions from '@/components/landing/institute-social-actions';
import SeoHead from '@/components/seo/SeoHead';
import { TablePagination } from '@/components/table-components';
import LandingLayout from '@/layouts/landing-layout';
import { Link, router } from '@inertiajs/react';
import { ChevronRight, Lightbulb } from 'lucide-react';
import { useMemo, useState } from 'react';
const baseUrl = import.meta.env.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

type Research = {
    id: number;
    title: string;
    category: string;
    date: string;
    img: string | null;
    link: string | null;
    sdgs?: Array<{ sdg_number: number; short_title: string; slug: string; image?: string | null }>;
};

type Activity = {
    id: number;
    title: string;
    date: string;
    img: string | null;
    pdf: string | null;
    sdgs?: Array<{ sdg_number: number; short_title: string; slug: string; image?: string | null }>;
};

type Institute = {
    id: number;
    name: string;
    logo: string | null;
    image: string | null;
    facebook_link?: string | null;
    email?: string | null;
};

type Props = {
    institute: Institute;
    researches: string;
    researchPagination: string;
    researchFilters: string;
    activities: string;
    activityPagination: string;
    tab: string;
};

type PaginationState = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
};

type ResearchFilters = {
    category: string | null;
};

const PER_PAGE = 8;
const categoryOptions = ['Student Research Published', 'Faculty Publication'];

export default function LandingEducation({
    institute,
    researches: encodedResearches,
    researchPagination: encodedResearchPagination,
    researchFilters: encodedResearchFilters,
    activities: encodedActivities,
    activityPagination: encodedActivityPagination,
    tab,
}: Props) {
    const [activeTab, setActiveTab] = useState<'about' | 'research' | 'activities'>(tab === 'research' || tab === 'activities' ? tab : 'about');

    const heroImage = institute.image ? `${baseUrl}/storage/institutes/images/${institute.image}` : null;
    const logoSrc = institute.logo ? `${baseUrl}/storage/institutes/logos/${institute.logo}` : null;
    const seoImage = heroImage || logoSrc || `${baseUrl}/images/logo.webp`;

    const researches = useMemo<Research[]>(() => {
        try {
            return JSON.parse(atob(encodedResearches));
        } catch {
            return [];
        }
    }, [encodedResearches]);

    const researchPagination = useMemo<PaginationState>(() => {
        try {
            const pagination = JSON.parse(atob(encodedResearchPagination));
            const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;
            return { ...pagination, last_page: lastPage };
        } catch {
            return { current_page: 1, per_page: PER_PAGE, last_page: 1, total: 0 };
        }
    }, [encodedResearchPagination]);

    const researchFilters = useMemo<ResearchFilters>(() => {
        try {
            return JSON.parse(atob(encodedResearchFilters));
        } catch {
            return { category: null };
        }
    }, [encodedResearchFilters]);

    const activities = useMemo<Activity[]>(() => {
        try {
            return JSON.parse(atob(encodedActivities));
        } catch {
            return [];
        }
    }, [encodedActivities]);

    const activityPagination = useMemo<PaginationState>(() => {
        try {
            const pagination = JSON.parse(atob(encodedActivityPagination));
            const lastPage = pagination.per_page > 0 ? Math.max(1, Math.ceil(pagination.total / pagination.per_page)) : 1;
            return { ...pagination, last_page: lastPage };
        } catch {
            return { current_page: 1, per_page: PER_PAGE, last_page: 1, total: 0 };
        }
    }, [encodedActivityPagination]);

    const researchMeta = useMemo(() => {
        const from = researchPagination.total === 0 ? 0 : (researchPagination.current_page - 1) * researchPagination.per_page + 1;
        const to =
            researchPagination.total === 0 ? 0 : Math.min(researchPagination.total, researchPagination.current_page * researchPagination.per_page);
        return { from, to };
    }, [researchPagination]);

    const activityMeta = useMemo(() => {
        const from = activityPagination.total === 0 ? 0 : (activityPagination.current_page - 1) * activityPagination.per_page + 1;
        const to =
            activityPagination.total === 0 ? 0 : Math.min(activityPagination.total, activityPagination.current_page * activityPagination.per_page);
        return { from, to };
    }, [activityPagination]);

    const handleChangeTab = (nextTab: 'about' | 'research' | 'activities') => {
        setActiveTab(nextTab);
        router.get(
            route('education'),
            {
                tab: nextTab,
                research_page: researchPagination.current_page,
                activity_page: activityPagination.current_page,
                research_category: researchFilters.category,
            },
            {
                preserveScroll: true,
                preserveState: true,
                replace: true,
            },
        );
    };

    const handleResearchPageChange = (page: number) => {
        router.get(
            route('education'),
            {
                tab: 'research',
                research_page: page,
                activity_page: activityPagination.current_page,
                research_category: researchFilters.category,
            },
            {
                preserveScroll: true,
                preserveState: true,
            },
        );
    };
    const handleSelectResearchCategory = (category: string | null) => {
        router.get(
            route('education'),
            {
                tab: 'research',
                research_category: category,
                research_page: 1,
                activity_page: activityPagination.current_page,
            },
            {
                preserveScroll: true,
                preserveState: true,
            },
        );
    };

    const handleActivityPageChange = (page: number) => {
        router.get(
            route('education'),
            {
                tab: 'activities',
                research_page: researchPagination.current_page,
                activity_page: page,
                research_category: researchFilters.category,
            },
            {
                preserveScroll: true,
                preserveState: true,
            },
        );
    };

    return (
        <LandingLayout>
            <SeoHead
                title={institute.name || 'College of Education'}
                description={`Learn about ${institute.name || 'the institute'}: philosophy, objectives, research, and activities at Northeastern College.`}
                image={seoImage}
            />

            <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                {heroImage ? (
                    <img
                        src={heroImage}
                        alt={institute.name + ' cover'}
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />
                ) : (
                    <img
                        src="/images/hero/nc1.png"
                        alt="Northeastern College campus"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-50"
                    />
                )}

                <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />

                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                <div className="pointer-events-none absolute top-1/4 left-1/4 h-64 w-64 rounded-full bg-emerald-500/10 blur-[100px]" />
                <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-64 w-64 rounded-full bg-emerald-500/5 blur-[100px]" />

                <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                    <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                        <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                            Home
                        </Link>
                        <ChevronRight className="size-4" aria-hidden="true" />
                        <span className="text-white/80">Institutes</span>
                        <ChevronRight className="size-4" aria-hidden="true" />
                        <span className="text-white/80">{institute.name}</span>
                    </div>

                    <div className="flex flex-col items-center gap-6 text-center md:flex-row md:text-left">
                        {logoSrc && (
                            <div className="border-border flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-white shadow-lg md:h-28 md:w-28">
                                <img src={logoSrc} alt={institute.name + ' logo'} className="h-[80%] w-[80%] rounded-full object-contain" />
                            </div>
                        )}
                        <div>
                            <h1 className="text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">{institute.name}</h1>
                            <InstituteSocialActions email={institute.email} facebookHref={institute.facebook_link} instituteName={institute.name} />
                            <p className="mt-4 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                                Academic excellence, research, and holistic student-centered development.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <section className="bg-muted relative z-20 py-12">
                <div className="mx-auto max-w-7xl px-4">
                    {/* Floating Navigation Card */}
                    <div className="relative z-20 mx-auto -mt-20 mb-10 max-w-7xl">
                        <div className="bg-card border-border flex flex-col items-center justify-center gap-6 rounded-2xl border p-4 shadow-sm md:flex-row md:p-6">
                            <div className="flex flex-wrap justify-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('about')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'about'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    About
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('research')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'research'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    Research
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleChangeTab('activities')}
                                    className={`flex shrink-0 items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-all duration-200 md:px-5 md:py-2.5 md:text-sm ${
                                        activeTab === 'activities'
                                            ? 'bg-[#0A7A35] text-white shadow-md'
                                            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                                    }`}
                                >
                                    Activities
                                </button>
                            </div>
                        </div>
                    </div>
                    {activeTab === 'about' && (
                        <div className="space-y-8">
                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/10">
                                    <Lightbulb className="h-6 w-6 text-emerald-600 dark:text-[#21d36b]" />
                                </div>
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Philosophy</h3>
                                <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
                                    The College of Education believes that every individual has the teacher instinct. With proper training on the art
                                    and science of teaching, one can be a better teacher. Processing the necessary knowledge, skills, attitudes, and
                                    values, a future teacher can efficiently and effectively deliver the goods to the educands. Thus the College of
                                    Education holistically prepares its students into the reality of education for them not only to be transmitters of
                                    knowledge but also creators of new knowledge.
                                </p>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Objectives</h3>
                                <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                    <li>Develop the true mentor committed to the service and the profession.</li>
                                    <li>
                                        Orient prospective teachers on the thrusts, developments, methodologies, and strategies responsive to the
                                        advancing global technology and trends.
                                    </li>
                                    <li>
                                        Train students as future leaders and arm of government in the intellectual, social, moral, and professional
                                        values of the country.
                                    </li>
                                    <li>
                                        Develop skills on curriculum planning, classroom management, and effective pedagogies conducive for their
                                        respective fields grounded with research-based approaches and methodologies.
                                    </li>
                                    <li>
                                        Incorporate the importance of professional development as part of their life-long learning in line with
                                        national and international qualifications frameworks.
                                    </li>
                                    <li>
                                        Provide varied and rich learning experiences that enhance students&apos; skills in communication and teaching.
                                    </li>
                                </ul>
                            </div>

                            <div className="bg-card border-border relative overflow-hidden rounded-2xl border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md md:p-7">
                                <h3 className="text-foreground mb-3 text-lg font-semibold md:text-xl">Program Outcomes</h3>
                                <ul className="text-muted-foreground list-inside list-decimal space-y-2 text-sm leading-relaxed md:text-base">
                                    <li>A reflective teacher who exhibits spiritual values and encompasses patriotic ideals.</li>
                                    <li>
                                        A globally competitive educand who has mastery of research-based pedagogical content and is imbued with a
                                        professional and wholesome personality.
                                    </li>
                                    <li>
                                        An educand who possesses impressive communication and teaching skills and is exposed to authentic learning
                                        environment.
                                    </li>
                                    <li>
                                        A lifelong learner who values hard work, professional ethics, and commitment to rendering services to others.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}

                    {activeTab === 'research' && (
                        <div>
                            <div className="mb-8 text-center">
                                <h2 className="text-foreground mb-2 text-2xl font-semibold tracking-tight md:text-3xl">Research</h2>
                                <p className="text-muted-foreground text-sm md:text-base">Research outputs from the College of Education.</p>
                            </div>
                            <div className="bg-card border-border mb-6 flex flex-col items-start justify-between gap-4 rounded-2xl border p-4 shadow-sm xl:flex-row xl:items-center xl:p-6">
                                <div className="flex w-full flex-wrap gap-2 xl:w-auto xl:shrink-0 xl:flex-nowrap">
                                    <button
                                        type="button"
                                        onClick={() => handleSelectResearchCategory(null)}
                                        className={`rounded-full px-5 py-2 text-xs font-semibold transition md:text-sm ${
                                            researchFilters.category === null
                                                ? 'bg-[#0A7A35] text-white shadow-sm'
                                                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                        }`}
                                    >
                                        All Categories
                                    </button>
                                    {categoryOptions.map((category) => (
                                        <button
                                            key={category}
                                            type="button"
                                            onClick={() => handleSelectResearchCategory(category)}
                                            className={`rounded-full px-5 py-2 text-xs font-semibold transition md:text-sm ${
                                                researchFilters.category === category
                                                    ? 'bg-[#0A7A35] text-white shadow-sm'
                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                            }`}
                                        >
                                            {category === 'Student Research Published' ? 'Student Research' : 'Faculty Research'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {researches.length === 0 ? (
                                <div className="bg-card border-border flex flex-col items-center justify-center rounded-2xl border px-4 py-20 text-center shadow-sm">
                                    <p className="text-muted-foreground text-sm md:text-base">No publications found for this institute yet.</p>
                                </div>
                            ) : (
                                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
                                    {researches.map((r) => (
                                        <ContentFeatureCard
                                            key={r.id}
                                            title={r.title}
                                            image={r.img}
                                            institute={institute.name}
                                            badges={r.category ? [r.category === 'Student Research Published' ? 'Student Research' : 'Faculty Research'] : []}
                                            date={r.date}
                                            linkHref={r.link}
                                            sdgs={r.sdgs}
                                        />
                                    ))}
                                </div>
                            )}

                            {researchPagination.total > 0 && (
                                <div className="mt-6">
                                    <TablePagination
                                        hidePerPage
                                        currentPage={researchPagination.current_page}
                                        lastPage={researchPagination.last_page}
                                        perPage={researchPagination.per_page}
                                        total={researchPagination.total}
                                        from={researchMeta.from}
                                        to={researchMeta.to}
                                        onPageChange={(page) => handleResearchPageChange(page)}
                                        onPerPageChange={() => {}}
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'activities' && (
                        <div>
                            <div className="mb-8 text-center">
                                <h2 className="text-foreground mb-2 text-2xl font-semibold tracking-tight md:text-3xl">Activities</h2>
                                <p className="text-muted-foreground text-sm md:text-base">Activities from the College of Education.</p>
                            </div>

                            {activities.length === 0 ? (
                                <div className="bg-card border-border flex flex-col items-center justify-center rounded-2xl border px-4 py-20 text-center shadow-sm">
                                    <p className="text-muted-foreground text-sm md:text-base">No activities available for this institute yet.</p>
                                </div>
                            ) : (
                                <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
                                    {activities.map((a) => (
                                        <ContentFeatureCard
                                            key={a.id}
                                            title={a.title}
                                            image={a.img}
                                            showInstitute={false}
                                            date={a.date}
                                            linkHref={a.pdf}
                                            sdgs={a.sdgs}
                                        />
                                    ))}
                                </div>
                            )}

                            {activityPagination.total > 0 && (
                                <div className="mt-6">
                                    <TablePagination
                                        hidePerPage
                                        currentPage={activityPagination.current_page}
                                        lastPage={activityPagination.last_page}
                                        perPage={activityPagination.per_page}
                                        total={activityPagination.total}
                                        from={activityMeta.from}
                                        to={activityMeta.to}
                                        onPageChange={(page) => handleActivityPageChange(page)}
                                        onPerPageChange={() => {}}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
